import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Section, SectionHeading, Eyebrow, PrimaryLink } from "@/components/common";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function CaseStudies() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/case-studies")
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Seo title="Case Studies" description="Approved ERAM DIGITAL case studies will be published here. Contact our team to discuss your requirements." path="/case-studies" />

      <Section className="py-20">
        <Reveal>
          <Eyebrow>Case studies</Eyebrow>
          <h1 className="max-w-4xl font-display text-5xl font-semibold leading-tight tracking-tighter text-[#0A0A0A] sm:text-6xl">
            Measurable outcomes, <span className="gradient-text">not marketing.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#4B5563]">
            We'll publish client stories here once the content and references are approved. Until then, our team can help you explore the right approach for your needs.
          </p>
        </Reveal>
      </Section>

      <Section className="pb-24">
        {loading ? (
          <div className="grid gap-6 lg:grid-cols-2">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-64 animate-pulse rounded-3xl border border-[#E5E7EB] bg-[#F9FAFB]" />)}
          </div>
        ) : items.length === 0 ? (
          <Reveal>
            <div className="rounded-3xl border border-[#E5E7EB] bg-[#F9FAFB] p-12 text-center" data-testid="cases-empty">
              <p className="font-display text-2xl font-bold text-[#0A0A0A]">Case studies coming soon.</p>
              <p className="mx-auto mt-3 max-w-xl text-[#4B5563]">We're not publishing client engagements until their details and references are approved. Please contact us to discuss your requirements.</p>
              <div className="mt-7 flex justify-center"><PrimaryLink to="/contact" testid="cases-empty-cta">Talk to our team</PrimaryLink></div>
            </div>
          </Reveal>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {items.map((cs, i) => (
              <Reveal key={cs.id} delay={(i % 2) * 0.08}>
                <div className="flex h-full flex-col rounded-3xl border border-[#E5E7EB] bg-white p-8 sm:p-10" data-testid={`case-study-${cs.id}`}>
                  <div className="flex items-center gap-3 text-xs font-medium uppercase tracking-widest">
                    <span className="text-brand-teal">{cs.industry}</span>
                    <span className="text-[#9CA3AF]">·</span>
                    <span className="text-[#4B5563]">{cs.pillar}</span>
                    <span className="ml-auto rounded-full border border-[#E5E7EB] px-2 py-0.5 text-[10px] text-[#4B5563]">Representative</span>
                  </div>
                  <h3 className="mt-4 font-display text-2xl font-semibold text-[#0A0A0A]">{cs.title}</h3>
                  <div className="mt-6 space-y-4 text-sm">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Challenge</p>
                      <p className="mt-1 leading-relaxed text-[#4B5563]">{cs.challenge}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Solution</p>
                      <p className="mt-1 leading-relaxed text-[#4B5563]">{cs.solution}</p>
                    </div>
                  </div>
                  <div className="mt-auto grid grid-cols-3 gap-3 pt-7">
                    {cs.results?.map((r, ri) => (
                      <div key={ri} className="rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] p-3 text-center">
                        <TrendingUp className="mx-auto mb-1 h-4 w-4 text-brand-teal" />
                        <p className="text-xs font-semibold leading-tight text-[#0A0A0A]">{r}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </Section>

      <Section className="pb-28">
        <Reveal>
          <div className="rounded-3xl border border-[#E5E7EB] bg-[#F9FAFB] p-10 text-center sm:p-14">
            <SectionHeading eyebrow="Your outcome next" title="Let's write your" gradient="success story." center />
            <div className="mt-8 flex justify-center"><PrimaryLink to="/contact" testid="cases-cta">{t("cta.getStarted")}</PrimaryLink></div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
